import { useContext } from "react"
import { GetServerSideProps } from "next"
import { TourProvider, StepType } from "@reactour/tour"

import AppContext from "@secureo/common/context/AppContext/AppContext"

import NotFoundPage from "pages/404"
import LockingSystemConfigurator from "containers/LockingSystem/LockingSystemConfigurator"

import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchLockingSystemCategory from "@secureo/common/utils/commerceTools/graphql/queries/fetchLockingSystemCategory"

import { LockingSystemCategory } from "@secureo/common/typings/Category"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import { CustomAppProps } from "pages/_app"

const steps: StepType[] = [
	{
		selector: "#locking-system-name",
		content: "Vergeben Sie einen sprechenden Namen für Ihre Schließanlage.",
		styles: {
			badge: (base) => ({ ...base, backgroundColor: "#4691ce" }),
		},
	},
	{
		selector: "#locking-system-toolbar",
		content: "Hier können Sie Ihre Konfiguration Zurücksetzen, Speichern und Teilen.",
		styles: {
			badge: (base) => ({ ...base, backgroundColor: "#4691ce" }),
		},
	},
	{
		selector: "#edit-locking-system-row",
		content: "Konfigurieren Sie den Zylinder nach Ihren Bedürfnissen.",
		styles: {
			badge: (base) => ({ ...base, backgroundColor: "#4691ce" }),
		},
	},
	{
		selector: "#edit-locking-system-key",
		content: "Konfigurieren Sie den Schlüssel nach Ihren Bedürfnissen.",
		styles: {
			badge: (base) => ({ ...base, backgroundColor: "#4691ce" }),
		},
	},
	{
		selector: "#locking-system-key-move",
		content: "Verschieben Sie den Schlüssel bei Bedarf nach links oder rechts.",
		styles: {
			badge: (base) => ({ ...base, backgroundColor: "#4691ce" }),
		},
	},
]

const LockingSystemCategoryPage = ({
	lockingSystemCategory,
	locale,
	countryCode,
	asPath,
}: Props & CustomAppProps) => {
	const { accessToken } = useContext(AppContext)

	if (!lockingSystemCategory) return <NotFoundPage />

	const css = `
	div[id^="trustbadge-container-"] {
		display: none;
	}`

	return (
		<TourProvider steps={steps}>
			<style>{css}</style>
			<LockingSystemConfigurator
				accessToken={accessToken}
				asPath={asPath}
				locale={locale}
				countryCode={countryCode}
				lockingSystemCategory={lockingSystemCategory}
			/>
		</TourProvider>
	)
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
	const { slug, locale: rawLocale } = ctx.params as {
		slug: string
		locale: string
	}

	const { locale, language, countryCode } = normalizeLocale(rawLocale)
	const key = slug ? `dzn-sa-${slug}` : null

	const accessToken = await anonymousAccessTokenCache()
	const lockingSystemCategory = await fetchLockingSystemCategory(accessToken, key, locale, "EUR")

	const props: Props = {
		locale,
		language,
		countryCode,
		lockingSystemCategory,
	}

	return { props }
}

interface Props {
	locale: string
	language: string
	countryCode: CountryCode
	lockingSystemCategory: LockingSystemCategory
}

export default LockingSystemCategoryPage
